import React, { useEffect, useRef } from 'react';
import {
  Plus, ArrowUp, ArrowDown, Pencil, Star, Copy, Users2, Link2,
  Activity, Trash2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useTeamsQuery } from '../hooks/queries';
import { useTeamRole, canEditInTeam } from '../hooks/useRole';

const ProjectContextMenu = ({
  show,
  x,
  y,
  project,
  onClose,
  onEdit,
  onDelete,
  onDuplicate,
  onMoveAbove,
  onMoveBelow,
  onToggleFavorite,
  onShare,
  onAddToTeam,
  onActivityLog,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const menuRef = useRef(null);

  // For team projects, allow delete to anyone with edit-all rights on the
  // owning team (owner / admin / team_manager), not just the project's
  // creator. Mirrors the backend's can_edit_all check in routes/projects.py.
  const teamsQuery = useTeamsQuery();
  const projectTeam = project?.team_id
    ? (teamsQuery.data || []).find((t) => t.id === project.team_id)
    : null;
  const projectTeamRole = useTeamRole(projectTeam);
  const canDeleteProject =
    user && project && (
      project.user_id === user.id ||
      (projectTeam && canEditInTeam(projectTeamRole))
    );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (show) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [show, onClose]);

  // Adjust position to stay within viewport
  useEffect(() => {
    if (show && menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let adjustedLeft = x;
      let adjustedTop = y;

      if (rect.right > viewportWidth) {
        adjustedLeft = x - rect.width;
      }
      if (rect.bottom > viewportHeight) {
        adjustedTop = y - rect.height;
      }
      // Clamp to viewport edges
      adjustedLeft = Math.max(8, adjustedLeft);
      adjustedTop = Math.max(8, adjustedTop);

      menuRef.current.style.left = `${adjustedLeft}px`;
      menuRef.current.style.top = `${adjustedTop}px`;
    }
  }, [show, x, y]);

  if (!show || !project) return null;

  const MenuItem = ({ icon: Icon, label, onClick, variant = 'default', disabled = false }) => {
    const handleClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled && onClick) {
        onClick();
      }
    };

    return (
      <button
        onClick={handleClick}
        disabled={disabled}
        className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
          variant === 'danger'
            ? 'text-red-400 hover:bg-red-500/10'
            : disabled
            ? 'text-muted-foreground/70 cursor-not-allowed'
            : 'text-foreground/80 hover:bg-accent'
        }`}
      >
        <Icon className="w-4 h-4" />
        <span>{label}</span>
      </button>
    );
  };

  const Divider = () => <div className="my-1 border-t border-border" />;

  return (
    <div
      ref={menuRef}
      className="fixed z-50 w-56 bg-popover border border-border rounded-lg shadow-xl py-1 overflow-y-auto max-h-[calc(100vh-16px)]"
      style={{ left: x, top: y }}
    >
      {/* Add project above/below */}
      <MenuItem
        icon={ArrowUp}
        label={t('projects.move_above')}
        onClick={() => { onMoveAbove?.(project); onClose(); }}
        disabled={!onMoveAbove}
      />
      <MenuItem
        icon={ArrowDown}
        label={t('projects.move_below')}
        onClick={() => { onMoveBelow?.(project); onClose(); }}
        disabled={!onMoveBelow}
      />

      <Divider />

      {/* Edit & Favorites */}
      <MenuItem
        icon={Pencil}
        label={t('common.edit')}
        onClick={onEdit}
      />
      <MenuItem
        icon={Star}
        label={project.is_favorite ? t('projects.remove_from_favorites') : t('projects.add_to_favorites')}
        onClick={() => { onToggleFavorite?.(project); onClose(); }}
        disabled={!onToggleFavorite}
      />
      <MenuItem
        icon={Copy}
        label={t('common.duplicate')}
        onClick={onDuplicate}
      />

      <Divider />

      {/* Sharing */}
      <MenuItem
        icon={Users2}
        label={t('common.share')}
        onClick={() => { onShare?.(project); onClose(); }}
        disabled={!onShare}
      />
      <MenuItem
        icon={Plus}
        label={t('projects.add_to_team')}
        onClick={() => {
          onAddToTeam?.(project);
          onClose();
        }}
        disabled={!onAddToTeam}
      />
      <MenuItem
        icon={Link2}
        label={t('projects.copy_link')}
        onClick={() => {
          const url = `${window.location.origin}/app/projects?project=${project.id}`;
          navigator.clipboard.writeText(url);
          onClose();
        }}
      />


      <Divider />

      {/* Activity */}
      <MenuItem
        icon={Activity}
        label={t('projects.activity_log')}
        onClick={() => { onActivityLog?.(project); onClose(); }}
        disabled={!onActivityLog}
      />

      <Divider />

      {/* Move to Trash — visible to the project owner, or anyone with
          edit-all rights on the project's team (mirrors backend check). */}
      {canDeleteProject && (
        <MenuItem
          icon={Trash2}
          label={t('tasks.move_to_trash')}
          onClick={onDelete}
        />
      )}
    </div>
  );
};

export default ProjectContextMenu;
